import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  acknowledgeAlert,
  fetchAlerts,
  fetchDeviceLogs,
  fetchDeviceUtilizationSeries,
  fetchSchoolOverview,
  fetchSpeedtestResults,
} from '../api/dashboard'
import { LOGS_TIME_RANGES, getTimeRangeParams } from '../utils/timeRanges'

const defaultSeries = { in: [], out: [] }
const MAX_PAGINATION_LIMIT = 100

/**
 * School page filter: MikroTik alerts match by router IP in `instance`;
 * Hikvision (and any SD-enriched) alerts match by `labels.school`.
 */
const normalizeAlertsForSchool = (alerts = [], { ip, schoolName } = {}) => {
  if (!ip && !schoolName) return []

  const schoolNorm = schoolName?.trim().toLowerCase()

  return alerts.filter((alert) => {
    const instance = alert.labels?.instance || alert.labels?.pod || alert.annotations?.instance
    if (ip && instance && String(instance).includes(ip)) return true

    const alertSchool = alert.labels?.school
    if (schoolNorm && alertSchool && String(alertSchool).trim().toLowerCase() === schoolNorm) {
      return true
    }

    return false
  })
}

const extractPaginatedRows = (result, page, pageSize) => {
  if (!result) {
    return { rows: [], total: 0 }
  }

  const apiData = Array.isArray(result.data) ? result.data : Array.isArray(result) ? result : []
  const pagination = result?.pagination

  if (pagination && Number.isFinite(Number(pagination.total))) {
    const total = Number(pagination.total)
    return {
      rows: apiData,
      total,
    }
  }

  const total = apiData.length
  const startIndex = Math.max(0, (page - 1) * pageSize)
  const endIndex = startIndex + pageSize
  return {
    rows: apiData.slice(startIndex, endIndex),
    total,
  }
}

export const useSchoolDashboardData = (ip, { utilizationRange = '24h', logsRange = '24h' } = {}) => {
  const [overview, setOverview] = useState(null)
  const [overviewLoading, setOverviewLoading] = useState(false)

  const [utilizationData, setUtilizationData] = useState(defaultSeries)
  const [utilizationLoading, setUtilizationLoading] = useState(false)

  const [alerts, setAlerts] = useState([])
  const [alertsLoading, setAlertsLoading] = useState(false)
  const [alertsPage, setAlertsPage] = useState(1)
  const [alertsPageSize, setAlertsPageSize] = useState(10)
  const [alertsTotalPages, setAlertsTotalPages] = useState(1)
  const [alertsTotalCount, setAlertsTotalCount] = useState(0)
  const [acknowledgeLoading, setAcknowledgeLoading] = useState(false)

  const [logs, setLogs] = useState([])
  const [logsLoading, setLogsLoading] = useState(false)
  const [logsPage, setLogsPage] = useState(1)
  const [logsTotalPages, setLogsTotalPages] = useState(1)
  const logsPageSize = 10

  const [speedtests, setSpeedtests] = useState([])
  const [speedtestsLoading, setSpeedtestsLoading] = useState(false)
  const [speedtestsPage, setSpeedtestsPage] = useState(1)
  const [speedtestsTotalPages, setSpeedtestsTotalPages] = useState(1)
  const speedtestsPageSize = 10

  useEffect(() => {
    setLogsPage(1)
  }, [ip, logsRange])

  useEffect(() => {
    setSpeedtestsPage(1)
  }, [ip])

  useEffect(() => {
    setAlertsPage(1)
  }, [ip, alertsPageSize])

  const loadOverview = useCallback(async () => {
    if (!ip) return
    setOverviewLoading(true)
    try {
      const result = await fetchSchoolOverview(ip)
      setOverview(result)
    } catch (error) {
      setOverview(null)
    } finally {
      setOverviewLoading(false)
    }
  }, [ip])

  const loadUtilization = useCallback(async () => {
    if (!ip) return
    setUtilizationLoading(true)
    try {
      const params = getTimeRangeParams(utilizationRange, LOGS_TIME_RANGES)
      const series = await fetchDeviceUtilizationSeries(ip, params)
      setUtilizationData(series)
    } catch (error) {
      setUtilizationData(defaultSeries)
    } finally {
      setUtilizationLoading(false)
    }
  }, [ip, utilizationRange])

  const schoolName = overview?.school || null

  const loadAlerts = useCallback(async () => {
    if (!ip) return
    setAlertsLoading(true)
    try {
      const data = await fetchAlerts()
      const filtered = normalizeAlertsForSchool(data, { ip, schoolName })
      const total = filtered.length
      const startIndex = Math.max(0, (alertsPage - 1) * alertsPageSize)
      const endIndex = startIndex + alertsPageSize
      setAlerts(filtered.slice(startIndex, endIndex))
      setAlertsTotalPages(Math.max(1, Math.ceil(total / alertsPageSize)))
      setAlertsTotalCount(total)
    } catch (error) {
      setAlerts([])
      setAlertsTotalPages(1)
      setAlertsTotalCount(0)
    } finally {
      setAlertsLoading(false)
    }
  }, [ip, schoolName, alertsPage, alertsPageSize])

  const loadLogs = useCallback(async () => {
    if (!ip) return
    setLogsLoading(true)
    try {
      const params = getTimeRangeParams(logsRange, LOGS_TIME_RANGES)
      const startIso = params.start ? new Date(params.start * 1000).toISOString() : undefined
      const endIso = params.end ? new Date(params.end * 1000).toISOString() : undefined

      const result = await fetchDeviceLogs({
        ip,
        ...(startIso ? { start: startIso } : {}),
        ...(endIso ? { end: endIso } : {}),
        limit: logsPageSize,
        offset: (logsPage - 1) * logsPageSize,
      })
      const { rows, total } = extractPaginatedRows(result, logsPage, logsPageSize)
      setLogs(rows)
      setLogsTotalPages(Math.max(1, Math.ceil(total / logsPageSize)))
    } catch (error) {
      setLogs([])
      setLogsTotalPages(1)
    } finally {
      setLogsLoading(false)
    }
  }, [ip, logsRange, logsPage])

  const loadSpeedtests = useCallback(async () => {
    if (!ip) return
    setSpeedtestsLoading(true)
    try {
      const result = await fetchSpeedtestResults({
        ip,
        limit: speedtestsPageSize,
        offset: (speedtestsPage - 1) * speedtestsPageSize,
      })
      const { rows, total } = extractPaginatedRows(result, speedtestsPage, speedtestsPageSize)
      setSpeedtests(rows)
      setSpeedtestsTotalPages(Math.max(1, Math.ceil(total / speedtestsPageSize)))
    } catch (error) {
      setSpeedtests([])
      setSpeedtestsTotalPages(1)
    } finally {
      setSpeedtestsLoading(false)
    }
  }, [ip, speedtestsPage])

  useEffect(() => {
    loadOverview()
  }, [loadOverview])

  useEffect(() => {
    loadUtilization()
  }, [loadUtilization])

  useEffect(() => {
    loadAlerts()
  }, [loadAlerts])

  useEffect(() => {
    loadLogs()
  }, [loadLogs])

  useEffect(() => {
    loadSpeedtests()
  }, [loadSpeedtests])

  const acknowledgeAlertByFingerprint = useCallback(
    async (fingerprint, options = {}) => {
      if (!fingerprint) {
        throw new Error('Не указан fingerprint алерта')
      }

      setAcknowledgeLoading(true)
      try {
        await acknowledgeAlert(fingerprint, options)
        await loadAlerts()
      } finally {
        setAcknowledgeLoading(false)
      }
    },
    [loadAlerts],
  )

  const fetchDeviceSeries = useCallback(
    async (deviceIp, range) => {
      if (!deviceIp) {
        throw new Error('Не указан IP устройства')
      }
      const params = getTimeRangeParams(range, LOGS_TIME_RANGES)
      return await fetchDeviceUtilizationSeries(deviceIp, params)
    },
    [],
  )

  const metadata = useMemo(
    () => ({
      overview,
      overviewLoading,
      utilizationRange,
      logsRange,
      alertsPageSize,
      alertsTotal: alertsTotalCount,
    }),
    [overview, overviewLoading, utilizationRange, logsRange, alertsPageSize, alertsTotalCount],
  )

  return {
    overview,
    overviewLoading,
    utilizationData,
    utilizationLoading,
    alerts,
    alertsLoading,
    alertsPage,
    alertsPageSize,
    alertsTotalPages,
    alertsTotalCount,
    logs,
    logsLoading,
    logsPage,
    logsTotalPages,
    speedtests,
    speedtestsLoading,
    speedtestsPage,
    speedtestsTotalPages,
    acknowledgeAlert: acknowledgeAlertByFingerprint,
    acknowledgeLoading,
    reloadOverview: loadOverview,
    reloadUtilization: loadUtilization,
    reloadAlerts: loadAlerts,
    reloadLogs: loadLogs,
    reloadSpeedtests: loadSpeedtests,
    setLogsPage,
    setSpeedtestsPage,
    setAlertsPage,
    setAlertsPageSize,
    logsPageSize,
    speedtestsPageSize,
    metadata,
    fetchDeviceSeries,
  }
}

export default useSchoolDashboardData

